<template>
  <div :class="$style.action">
    <component :is="TimeSlotPickerModal">
      <TimeSlotPickerModalComponent
        v-model="pickedTimeSlots"
        v-model:fallbackUrl="fallbackUrl"
        @onCloseClicked="closeTimeSlotPickerModal"
        @onPickingTimeSlotDoneClicked="closeTimeSlotPickerModal"
        @onCalendarAuthError="handleCalendarAuthError"
      />
    </component>

    <div :class="[$style.action__inputs, $style.inputs]">
      <div :class="$style.inputs__textarea">
        <ChatTemplateModal>
          <ChatMessageTemplateModal
            @close="closeChatTemplateModal"
            @insert="applyTemplate"
          />
        </ChatTemplateModal>
        <FlatButton size="small" variant="base" @click="openChatTemplateModal">
          テンプレートの利用
        </FlatButton>
        <TextAreaWithAttachments
          :class="$style.textarea"
          v-model="text"
          :rows="textRows"
          @keyup.ctrl.enter="sendText"
        >
          <template #attachments v-if="!!pickedTimeSlots.length">
            <CardLikeContainer
              @click="openTimeSlotPickerModal"
              :class="[$style.textarea__attachmentCard, $style.card]"
            >
              <IconCalendar :class="$style.card__icon" />
              {{ pickedTimeSlots.length }}件の日程候補
            </CardLikeContainer>
          </template>
        </TextAreaWithAttachments>
        <p
          :class="[$style.inputs__count, $style.count, $style['count--error']]"
          v-show="isLineBlocked"
        >
          LINE上でブロックされているため、送信してもメッセージは届きません
        </p>
        <p
          :class="[$style.inputs__count, $style.count, $style['count--error']]"
          v-show="isTextByteLengthLimitExceeded"
        >
          {{ textByteLength }} /
          {{ MAX_BYTE_LEGNTH }} (最大文字データサイズを超えています)
        </p>
        <div :class="[$style.util, $style.inputs__util]">
          <MessageAttachmentActions
            v-model="selections"
            :pickedTimeSlots="pickedTimeSlots"
            @onClickPickTimeSlot="openTimeSlotPickerModal"
            :shouldShowTimeSlotPickerButton="true"
          />
        </div>
      </div>
    </div>
    <div :class="$style.action__triggers">
      <FlatButton
        :disabled="!isSubmittable"
        @click="sendText"
        variation="primary"
        size="medium"
      >
        送信
      </FlatButton>

      <div v-if="showSendAsStaffButton" :class="$style.action__checkbox">
        <DolphinCheckbox v-model="sendAsStaff" />
        <span>個人として発言</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'

import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'
import ChatMessageTemplateModal from '../components/ChatMessageTemplateModal/ChatMessageTemplateModal.vue'
import { useModal } from '@/hooks/useModal'

import type { ChatMessageTemplate } from '@/types/app'
import FlatButton from '@/components/atoms/FlatButton.vue'
import type { PickedTimeSlots } from '@/components/organisms/TimeSlotPickerModal/useTimeSlot'
import TextAreaWithAttachments from '@/components/atoms/TextAreaWithAttachments.vue'
import CardLikeContainer from '@/components/atoms/CardLikeContainer.vue'
import TimeSlotPickerModalComponent, {
  type AvailableBookingRequestTimeSlotsError,
} from '@/components/organisms/TimeSlotPickerModal/TimeSlotPickerModal.vue'
import IconCalendar from '@/components/icons/IconCalendar.vue'

const router = useRouter()

const props = defineProps<{
  pickedTimeSlots: PickedTimeSlots
  isLineBlocked: boolean
  fallbackUrl?: Maybe<string>
  text: string
  selections: string[]
  isSending: boolean
  showSendAsStaffButton: boolean
}>()

const sendAsStaff = defineModel<boolean>('sendAsStaff', {
  required: true,
})

const pickedTimeSlots = computed({
  get() {
    return props.pickedTimeSlots
  },
  set(value) {
    emit('update:pickedTimeSlots', value)
  },
})

const encoder = new TextEncoder()
const MAX_BYTE_LEGNTH = 5000
const textByteLength = computed(() => encoder.encode(text.value).byteLength)
const isTextByteLengthLimitExceeded = computed(
  () => textByteLength.value > MAX_BYTE_LEGNTH
)

const fallbackUrl = computed({
  get(): Maybe<string> {
    return props.fallbackUrl
  },
  set(v: Maybe<string>) {
    emit('update:fallbackUrl', v)
  },
})

const emit = defineEmits<{
  (event: 'update:text', value: string): void
  (event: 'update:pickedTimeSlots', value: PickedTimeSlots): void
  (event: 'update:fallbackUrl', value: Maybe<string>): void
  (event: 'update:selections', value: string[]): void
  (event: 'typing', value: boolean): void
  (event: 'send'): void
  (event: 'closeTemplateModal'): void
  (event: 'changeInput', hasInput: boolean): void
}>()

const { openChatTemplateModal, closeChatTemplateModal, ChatTemplateModal } =
  useModal('chatTemplate')

const {
  TimeSlotPickerModal,
  openTimeSlotPickerModal,
  closeTimeSlotPickerModal,
} = useModal('TimeSlotPicker' as const)

const handleCalendarAuthError = (
  err: AvailableBookingRequestTimeSlotsError
) => {
  switch (err) {
    case 'AvailableBookingRequestTimeSlotsErrorNoCalendarConnected':
      if (
        window.confirm(
          'カレンダーが連携されていません。連携設定ページで連携してください。'
        )
      ) {
        // eslint-disable-next-line fp/no-mutating-methods
        router.push('/settings/calendar')
      } else {
        closeTimeSlotPickerModal()
      }
      break
    case 'AvailableBookingRequestTimeSlotsErrorInvalidCalendarConnection':
      if (
        window.confirm(
          'カレンダー連携でエラーが発生しました。連携設定ページで再度連携してください。'
        )
      ) {
        // eslint-disable-next-line fp/no-mutating-methods
        router.push('/settings/calendar')
      } else {
        closeTimeSlotPickerModal()
      }
      break
  }
}

const text = computed<string>({
  get() {
    return props.text
  },
  set(value) {
    emit('update:text', value)
  },
})

const selections = computed<string[]>({
  get() {
    return props.selections
  },
  set(value) {
    emit('update:selections', value)
  },
})

watch([text, selections], ([t, s]) => {
  const hasInput = t.length > 0 || s.some(s => s.length > 0)
  emit('changeInput', hasInput)
})

watch(text, value => {
  emit('typing', value.length > 0)
})

const textRows = computed(
  (): number => (text.value.match(/\n/g)?.length ?? 1) + 2
)

const isSubmittable = computed(
  (): boolean =>
    text.value !== '' &&
    selections.value.every(content => content.trim() !== '') &&
    !props.isSending &&
    !isTextByteLengthLimitExceeded.value
)

const applyTemplate = (template: ChatMessageTemplate) => {
  // すでに入力済みの本文がある場合は、追加でテンプレ本文を下に付け足す
  text.value =
    text.value !== '' ? `${text.value}\n${template.body}` : template.body

  // 選択肢については既存の内容を消して上書きする
  selections.value = [...template.selections]

  closeChatTemplateModal()
}

const sendText = (): void => {
  selections.value = selections.value.filter(selection => selection !== '')
  emit('send')
}
</script>

<style lang="scss" module>
$border: 1px solid $color-border-low-emphasis;

.action {
  display: flex;

  &__inputs {
    flex: 1;
  }

  &__triggers {
    &:nth-child(n + 2) {
      margin-left: 32px;
    }
  }

  &__checkbox {
    margin-top: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.inputs {
  display: flex;

  &__textarea {
    flex: 1;
  }

  &__util {
    margin-top: 16px;
  }

  &__count {
    margin-top: 8px;
  }
}

.count {
  font-size: 10px;
  color: $color-text-and-object-low-emphasis;

  &--error {
    color: $color-caution-accent;
  }
}

.textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 20px;
  border: $border;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-base;
  color: $color-text-and-object-high-emphasis;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.4;
  resize: none;

  &:nth-child(n + 2) {
    margin-top: 12px;
  }

  &__interim {
    color: $color-text-and-object-accent-primary;
  }

  &__attachmentCard {
    width: fit-content;
  }
}

.card {
  display: flex;
  align-items: center;
  font-weight: 700;

  &__icon {
    font-size: 15px;
    margin-right: 8px;
  }
}

.util {
  display: flex;
  gap: 24px;
}
</style>
